/**
 * /me/leave — employee leave page (PR 4b, ADR 0008).
 *
 * Balance cards at the top, request-leave form in the middle,
 * my-requests table at the bottom. Cancel button per row where
 * the request status allows.
 */
import { createRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { LeaveRequestResponse } from '@kora/types';
// Value import (also carries the type) so FormSchema can reuse the shared
// LeaveType enum instead of a hand-maintained duplicate that drifts.
import { type LeaveType, LeaveTypeKey } from '@kora/types';
import { meLayoutRoute } from './meLayout.js';
import {
  cancelLeaveRequest,
  getMyLeaveBalance,
  listLeaveRequests,
  previewLeaveDays,
  submitLeaveRequest,
} from '../api/leave.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { formatDate } from '../lib/format.js';

export const myLeaveRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me/leave',
  component: MyLeavePage,
});

function MyLeavePage(): JSX.Element {
  const balanceQuery = useQuery({
    queryKey: ['me', 'leave', 'balance'],
    queryFn: getMyLeaveBalance,
  });
  const requestsQuery = useQuery({
    queryKey: ['leave', 'requests', 'mine'],
    queryFn: () => listLeaveRequests({ view: 'mine' }),
  });

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <header className="space-y-2">
        <Link
          to="/me"
          className="text-xs text-gray-500 hover:text-white underline-offset-2 hover:underline"
        >
          ← Back to dashboard
        </Link>
        <h1 className="font-serif text-3xl">My leave</h1>
      </header>

      <BalanceSection
        loading={balanceQuery.isLoading}
        balances={balanceQuery.data?.balances ?? []}
        error={balanceQuery.isError}
      />

      <RequestForm
        // ADR-0023 Phase 3b — custom types are now submittable, so the dropdown
        // offers every type the employee has a balance row for (built-in ∪
        // active custom). The server's `enabled` gate is the real guard.
        enabledTypes={(balanceQuery.data?.balances ?? []).map((b) => b.leaveType)}
      />

      <RequestsTable
        loading={requestsQuery.isLoading}
        requests={requestsQuery.data?.requests ?? []}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Balance cards
// ---------------------------------------------------------------------------

const LEAVE_LABEL: Record<LeaveType, string> = {
  annual: 'Annual',
  sick: 'Sick',
  maternity: 'Maternity',
  paternity: 'Paternity',
  compassionate: 'Compassionate',
  unpaid: 'Unpaid',
  casual: 'Casual',
  study: 'Study',
  other: 'Other',
};
// ADR-0023 Phase 3a — a leaveType may be an org-defined custom key. Label via
// the built-in map, else humanize the key ("exam_leave" → "Exam Leave").
const humanizeLeaveKey = (k: string): string => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const leaveLabel = (key: string): string => (LEAVE_LABEL as Record<string, string>)[key] ?? humanizeLeaveKey(key);

function BalanceSection({
  loading,
  balances,
  error,
}: {
  loading: boolean;
  balances: ReadonlyArray<{
    // ADR-0023 Phase 3a — may be an org-defined custom key (string), labelled
    // via leaveLabel() with a humanize fallback.
    leaveType: string;
    available: number;
    accrued: number;
    used: number;
    pending: number;
  }>;
  error: boolean;
}): JSX.Element {
  if (loading) {
    return (
      <div className="rounded border border-gray-900 p-6 text-center text-gray-500 text-sm">
        Loading balances…
      </div>
    );
  }
  if (error) {
    return (
      <Alert variant="error">Could not load your balances.</Alert>
    );
  }
  return (
    <section className="rounded border border-gray-900 divide-y divide-gray-900">
      <header className="px-4 py-3 text-xs uppercase tracking-wide text-gray-400">
        Balance
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4">
        {balances.map((b) => (
          <div
            key={b.leaveType}
            className="rounded border border-gray-800 bg-gray-950 p-3"
          >
            <div className="text-xs text-gray-500">{leaveLabel(b.leaveType)}</div>
            <div className="font-mono text-lg text-white">
              {b.available.toFixed(1)}
              <span className="text-xs text-gray-500"> days</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {b.used.toFixed(1)} used · {b.pending.toFixed(1)} pending
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Submit-request form
// ---------------------------------------------------------------------------

const FormSchema = z
  .object({
    // ADR-0023 Phase 3b — a key string (built-in ∪ custom), not the enum, so
    // org-defined types are submittable. The server's resolver `enabled` gate
    // is the authoritative check on whether the key is actually offered.
    leaveType: LeaveTypeKey,
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
    startDayPart: z.enum(['full', 'morning_half', 'afternoon_half']),
    endDayPart: z.enum(['full', 'morning_half', 'afternoon_half']),
    reason: z.string().max(500).optional(),
  })
  .refine((v) => v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'endDate must be on or after startDate',
  });
type FormValues = z.infer<typeof FormSchema>;

function RequestForm({ enabledTypes }: { enabledTypes: string[] }): JSX.Element {
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [preview, setPreview] = useState<number | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      leaveType: enabledTypes[0] ?? 'annual',
      startDate: '',
      endDate: '',
      startDayPart: 'full',
      endDayPart: 'full',
      reason: '',
    },
  });

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const startDayPart = watch('startDayPart');
  const endDayPart = watch('endDayPart');

  // Live working-days preview when the date inputs are populated.
  useEffect(() => {
    if (!startDate || !endDate || endDate < startDate) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    previewLeaveDays({ startDate, endDate, startDayPart, endDayPart })
      .then((r) => {
        if (!cancelled) setPreview(r.workingDaysCount);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, startDayPart, endDayPart]);

  const mutation = useMutation({
    mutationFn: submitLeaveRequest,
    onSuccess: async () => {
      setSuccess(true);
      reset();
      setPreview(null);
      await queryClient.invalidateQueries({ queryKey: ['leave', 'requests', 'mine'] });
      await queryClient.invalidateQueries({ queryKey: ['me', 'leave', 'balance'] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: string; message?: string; available?: number; requested?: number }
          | null;
        setServerError(body?.message ?? `Could not submit (HTTP ${err.status}).`);
      } else {
        setServerError('Could not submit.');
      }
    },
  });

  async function onSubmit(values: FormValues): Promise<void> {
    setServerError(null);
    setSuccess(false);
    mutation.mutate({
      leaveType: values.leaveType,
      startDate: values.startDate,
      endDate: values.endDate,
      startDayPart: values.startDayPart,
      endDayPart: values.endDayPart,
      ...(values.reason ? { reason: values.reason } : {}),
    });
  }

  return (
    <section className="rounded border border-gray-900 divide-y divide-gray-900">
      <header className="px-4 py-3 text-xs uppercase tracking-wide text-gray-400">
        Request leave
      </header>
      <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-3" noValidate>
        {success ? (
          <div
            role="status"
            className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300"
          >
            Request submitted. Your approver has been notified.
          </div>
        ) : null}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs text-gray-400 uppercase tracking-wide">
            Type
            <select
              {...register('leaveType')}
              className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
            >
              {enabledTypes.map((t) => (
                <option key={t} value={t}>
                  {leaveLabel(t)}
                </option>
              ))}
            </select>
          </label>
          <div className="text-xs text-gray-400 uppercase tracking-wide">
            Working days
            <div className="mt-1 rounded border border-gray-800 bg-gray-950 px-3 py-2 font-mono text-sm">
              {preview === null ? '—' : `${preview.toFixed(1)} day${preview === 1 ? '' : 's'}`}
            </div>
          </div>
          <label className="text-xs text-gray-400 uppercase tracking-wide">
            Start date
            <input
              type="date"
              {...register('startDate')}
              className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
            />
            {errors.startDate ? (
              <span className="text-xs text-red-400">{errors.startDate.message}</span>
            ) : null}
          </label>
          <label className="text-xs text-gray-400 uppercase tracking-wide">
            End date
            <input
              type="date"
              {...register('endDate')}
              className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
            />
            {errors.endDate ? (
              <span className="text-xs text-red-400">{errors.endDate.message}</span>
            ) : null}
          </label>
          <label className="text-xs text-gray-400 uppercase tracking-wide">
            Start day
            <select
              {...register('startDayPart')}
              className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
            >
              <option value="full">Full day</option>
              <option value="morning_half">Morning only</option>
              <option value="afternoon_half">Afternoon only</option>
            </select>
          </label>
          <label className="text-xs text-gray-400 uppercase tracking-wide">
            End day
            <select
              {...register('endDayPart')}
              className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
            >
              <option value="full">Full day</option>
              <option value="morning_half">Morning only</option>
              <option value="afternoon_half">Afternoon only</option>
            </select>
          </label>
        </div>
        <label className="block text-xs text-gray-400 uppercase tracking-wide">
          Reason (optional)
          <textarea
            {...register('reason')}
            rows={2}
            className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
          />
        </label>
        {serverError ? (
          <Alert variant="error">{serverError}</Alert>
        ) : null}
        <button
          type="submit"
          disabled={isSubmitting || mutation.isPending}
          className="rounded bg-white text-black text-sm font-medium px-4 py-2 disabled:opacity-50"
        >
          {mutation.isPending ? 'Submitting…' : 'Submit request'}
        </button>
      </form>
    </section>
  );
}

// ---------------------------------------------------------------------------
// My requests table
// ---------------------------------------------------------------------------

function RequestsTable({
  loading,
  requests,
}: {
  loading: boolean;
  requests: LeaveRequestResponse[];
}): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const cancelMut = useMutation({
    mutationFn: (id: string) => cancelLeaveRequest(id),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['leave', 'requests', 'mine'] });
      await queryClient.invalidateQueries({ queryKey: ['me', 'leave', 'balance'] });
    },
    onError: () => setError('Could not cancel — refresh and try again.'),
  });

  if (loading) {
    return (
      <div className="rounded border border-gray-900 p-6 text-center text-gray-500 text-sm">
        Loading requests…
      </div>
    );
  }
  if (requests.length === 0) {
    return (
      <section className="rounded border border-gray-900 p-6 text-center text-sm text-gray-500">
        No leave requests yet.
      </section>
    );
  }
  return (
    <section className="rounded border border-gray-900 overflow-hidden">
      <header className="px-4 py-3 text-xs uppercase tracking-wide text-gray-400 border-b border-gray-900">
        My requests
      </header>
      {error ? (
        <div className="px-4 py-2"><Alert variant="error" compact>{error}</Alert></div>
      ) : null}
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="text-left px-4 py-2">Type</th>
            <th className="text-left px-4 py-2">Dates</th>
            <th className="text-left px-4 py-2">Days</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-right px-4 py-2">Action</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.id} className="border-t border-gray-900">
              <td className="px-4 py-3 text-gray-300">
                {leaveLabel(r.leaveType)}
              </td>
              <td className="px-4 py-3 text-gray-300 font-mono text-xs">
                {formatDate(r.startDate)} → {formatDate(r.endDate)}
              </td>
              <td className="px-4 py-3 font-mono">{r.workingDaysCount.toFixed(1)}</td>
              <td className="px-4 py-3">
                <StatusPill status={r.status} />
              </td>
              <td className="px-4 py-3 text-right">
                {canCancel(r.status) ? (
                  <button
                    type="button"
                    onClick={() => cancelMut.mutate(r.id)}
                    disabled={cancelMut.isPending}
                    className="text-xs rounded border border-gray-700 px-2 py-1 hover:bg-gray-900 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                ) : (
                  <span className="text-xs text-gray-700">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function canCancel(status: string): boolean {
  return status === 'draft' || status === 'submitted' || status === 'approved';
}

function StatusPill({ status }: { status: string }): JSX.Element {
  const map: Record<string, string> = {
    draft: 'bg-gray-900 text-gray-400 border-gray-800',
    submitted: 'bg-yellow-950 text-yellow-400 border-yellow-900',
    approved: 'bg-green-950 text-green-400 border-green-900',
    rejected: 'bg-red-950 text-red-400 border-red-900',
    cancelled: 'bg-gray-900 text-gray-500 border-gray-800',
  };
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded border ${map[status] ?? 'border-gray-800'}`}
    >
      {status}
    </span>
  );
}
